import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  Loader2, Check, Users, Building2, PartyPopper, Music, Palette, 
  Plus, X, Tag, Briefcase, Phone, Mail, User
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface VendorContact {
  type: string;
  name: string;
  phone: string;
  email: string;
}

interface DatabaseContact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  category: string;
  tags: string[];
  notes: string | null;
  inquiry_id: string | null;
  created_at: string;
}

interface VendorContactsPanelProps {
  inquiryId: string;
  initialContacts: VendorContact[] | null;
}

const VENDOR_TYPES = [
  { type: 'venue', label: 'Venue POC', icon: Building2 },
  { type: 'planner', label: 'Wedding Planner', icon: PartyPopper },
  { type: 'dj', label: 'DJ / Production', icon: Music },
  { type: 'decor', label: 'Decor', icon: Palette },
];

const CATEGORY_OPTIONS = [
  'Venue',
  'Planner',
  'DJ/Production',
  'Decor',
  'Catering',
  'Photography',
  'Videography',
  'Florist',
  'Lighting',
  'Client',
  'Family',
  'Other',
];

export function VendorContactsPanel({ inquiryId, initialContacts }: VendorContactsPanelProps) {
  // Legacy contacts (stored in JSON on contact_submissions)
  const [contacts, setContacts] = useState<VendorContact[]>(() => {
    return VENDOR_TYPES.map(v => {
      const existing = initialContacts?.find(c => c.type === v.type);
      return existing || { type: v.type, name: '', phone: '', email: '' };
    });
  });
  
  // New database contacts
  const [dbContacts, setDbContacts] = useState<DatabaseContact[]>([]);
  const [loadingDb, setLoadingDb] = useState(true);
  
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: 'Other',
    tags: [] as string[],
    notes: '',
  });
  const [addingContact, setAddingContact] = useState(false);
  
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch database contacts
  const fetchDbContacts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('vendor_contacts')
        .select('*')
        .eq('inquiry_id', inquiryId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      setDbContacts(data || []);
    } catch (err) {
      console.error('Error fetching vendor contacts:', err);
    } finally {
      setLoadingDb(false);
    }
  }, [inquiryId]);

  useEffect(() => {
    fetchDbContacts();
  }, [fetchDbContacts]);

  const getCategoryFromType = (type: string): string => {
    const mapping: Record<string, string> = {
      'venue': 'Venue',
      'planner': 'Planner',
      'dj': 'DJ/Production',
      'decor': 'Decor',
    };
    return mapping[type] || 'Other';
  };

  const saveContactToDatabase = useCallback(async (contact: VendorContact) => {
    if (!contact.name.trim()) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const category = getCategoryFromType(contact.type);
      
      // Check if contact already exists in database for this inquiry and category
      const { data: existing } = await supabase
        .from('vendor_contacts')
        .select('id')
        .eq('inquiry_id', inquiryId)
        .eq('category', category)
        .maybeSingle();
      
      if (existing) {
        // Update existing
        await supabase
          .from('vendor_contacts')
          .update({
            name: contact.name.trim(),
            email: contact.email.trim() || null,
            phone: contact.phone.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
      } else {
        // Insert new
        await supabase
          .from('vendor_contacts')
          .insert({
            name: contact.name.trim(),
            email: contact.email.trim() || null,
            phone: contact.phone.trim() || null,
            category,
            inquiry_id: inquiryId,
            created_by: user?.id || null,
          });
      }
      
      // Refresh the database contacts list
      fetchDbContacts();
    } catch (err) {
      console.error('Error saving contact to database:', err);
    }
  }, [inquiryId, fetchDbContacts]);

  const saveContacts = useCallback(async (data: VendorContact[]) => {
    setSaving(true);
    setSaved(false);
    try {
      const filledContacts = data.filter(c => c.name || c.phone || c.email);
      
      // Save to JSON column (legacy)
      const { error } = await supabase
        .from('contact_submissions')
        .update({ vendor_contacts_json: filledContacts as unknown as any })
        .eq('id', inquiryId);
      
      if (error) throw error;
      
      // Also save each filled contact to the database
      for (const contact of filledContacts) {
        await saveContactToDatabase(contact);
      }
      
      // Auto-transition status to 'contacted' if currently pending or call_scheduled
      const { data: inquiry } = await supabase
        .from('contact_submissions')
        .select('status')
        .eq('id', inquiryId)
        .single();
      
      if (inquiry && ['pending', 'call_scheduled'].includes(inquiry.status)) {
        await supabase
          .from('contact_submissions')
          .update({ status: 'contacted' })
          .eq('id', inquiryId);
        
        // Log the activity (import logActivity at top of file)
        await supabase.from('inquiry_activity_logs').insert({
          inquiry_id: inquiryId,
          action_type: 'status_changed',
          action_description: 'Status changed to Contacted (vendor contacts activity)',
          metadata: { oldStatus: inquiry.status, newStatus: 'contacted', trigger: 'vendor_contact_edit' }
        });
      }
      
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      toast.error('Failed to save contacts');
    } finally {
      setSaving(false);
    }
  }, [inquiryId, saveContactToDatabase]);

  const handleChange = (type: string, field: keyof VendorContact, value: string) => {
    const updated = contacts.map(c => 
      c.type === type ? { ...c, [field]: value } : c
    );
    setContacts(updated);
    setSaved(false);
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveContacts(updated);
    }, 1000);
  };

  const handleAddContact = async () => {
    if (!newContact.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setAddingContact(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('vendor_contacts')
        .insert({
          name: newContact.name.trim(),
          email: newContact.email.trim() || null,
          phone: newContact.phone.trim() || null,
          company: newContact.company.trim() || null,
          category: newContact.category,
          tags: newContact.tags,
          notes: newContact.notes.trim() || null,
          inquiry_id: inquiryId,
          created_by: user?.id || null,
        });
      
      if (error) throw error;
      
      toast.success('Contact added to database');
      setShowAddDialog(false);
      setNewContact({ name: '', email: '', phone: '', company: '', category: 'Other', tags: [], notes: '' });
      fetchDbContacts();
    } catch (err: any) {
      toast.error('Failed to add contact: ' + err.message);
    } finally {
      setAddingContact(false);
    }
  };

  const handleDeleteDbContact = async (id: string) => {
    try {
      const { error } = await supabase
        .from('vendor_contacts')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      setDbContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Contact removed');
    } catch (err: any) {
      toast.error('Failed to delete contact');
    }
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <Users className="h-4 w-4" /> Vendor Contacts
        </h4>
        <div className="flex items-center gap-2">
          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
          {saved && <><Check className="h-3 w-3 text-primary" /></>}
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" /> Add Contact to Database
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
                    <Input
                      placeholder="Full name"
                      value={newContact.name}
                      onChange={e => setNewContact(p => ({ ...p, name: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Category</label>
                    <Select value={newContact.category} onValueChange={v => setNewContact(p => ({ ...p, category: v }))}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Email</label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={newContact.email}
                      onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Phone</label>
                    <Input
                      placeholder="(555) 123-4567"
                      value={newContact.phone}
                      onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Company</label>
                  <Input
                    placeholder="Company name"
                    value={newContact.company}
                    onChange={e => setNewContact(p => ({ ...p, company: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                  <Input
                    placeholder="Additional notes..."
                    value={newContact.notes}
                    onChange={e => setNewContact(p => ({ ...p, notes: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
                <Button onClick={handleAddContact} disabled={addingContact} className="w-full h-8">
                  {addingContact ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Save to Vendor Database
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <ScrollArea className="h-[280px]">
        <div className="space-y-2 pr-2">
          {/* Legacy fixed vendor types */}
          {VENDOR_TYPES.map(({ type, label, icon: Icon }) => {
            const contact = contacts.find(c => c.type === type) || { type, name: '', phone: '', email: '' };
            const hasData = contact.name || contact.phone || contact.email;
            
            return (
              <div 
                key={type} 
                className={`p-2 rounded-md border ${hasData ? 'bg-muted/30' : 'bg-transparent'} cursor-grab active:cursor-grabbing`}
                draggable={!!hasData}
                onDragStart={(e) => {
                  if (hasData) {
                    e.dataTransfer.setData("vendor/contact", JSON.stringify({
                      name: contact.name,
                      email: contact.email,
                      type: contact.type
                    }));
                  }
                }}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-medium">{label}</span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <Input
                    placeholder="Name"
                    value={contact.name}
                    onChange={(e) => handleChange(type, 'name', e.target.value)}
                    className="h-7 text-xs"
                  />
                  <Input
                    placeholder="Phone"
                    value={contact.phone}
                    onChange={(e) => handleChange(type, 'phone', e.target.value)}
                    className="h-7 text-xs"
                  />
                  <Input
                    placeholder="Email"
                    value={contact.email}
                    onChange={(e) => handleChange(type, 'email', e.target.value)}
                    className="h-7 text-xs"
                  />
                </div>
              </div>
            );
          })}
          
          {/* Database contacts from vendor_contacts table */}
          {dbContacts.length > 0 && (
            <div className="pt-2 border-t mt-2">
              <div className="text-[10px] text-muted-foreground mb-2 flex items-center gap-1">
                <Tag className="h-3 w-3" /> Saved to Database ({dbContacts.length})
              </div>
              {dbContacts.map((c) => (
                <div 
                  key={c.id} 
                  className="p-2 rounded-md border bg-primary/5 mb-1.5 group cursor-grab active:cursor-grabbing"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("vendor/contact", JSON.stringify({
                      name: c.name,
                      email: c.email,
                      type: 'database'
                    }));
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <User className="h-3 w-3 text-primary" />
                      <span className="text-xs font-medium">{c.name}</span>
                      <Badge variant="outline" className="text-[8px] h-4 px-1">{c.category}</Badge>
                    </div>
                    <button
                      onClick={() => handleDeleteDbContact(c.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 text-[10px] text-muted-foreground">
                    {c.company && (
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-2.5 w-2.5" />
                        <span className="truncate">{c.company}</span>
                      </div>
                    )}
                    {c.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-2.5 w-2.5" />
                        <span className="truncate">{c.phone}</span>
                      </div>
                    )}
                    {c.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-2.5 w-2.5" />
                        <span className="truncate">{c.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {loadingDb && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
